import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import media from 'styled-media-query';
import { useUser } from '../../../../../../redux/user/useUser';
import { Fab } from '../../../../../atoms/Fab';
import { PageWrapper } from '../../../../../atoms/PageWrapper';
import { Breadcrumb } from '../../../../../organisms/Breadcrumb';
import { ProgramElementsList } from '../../ProgramElementsList';
import { QuestionCreateModal } from '../../QuestionCreateModal';
import { QuestionTabContent } from '../../../../../organisms/QuestionTabContent';
import TabNavigationLayout from '../../../../../templates/TabNavigationLayout';
import { NotFoundPage } from '../../../../public/NotFound';
import { getChaptersAndPractices } from '../../programUtil';
import { LOWER_META_TITLE } from '../../../../../../const/Service';
import { FunctionType, PermissionType } from '../../../../../../const/UserPermission';
import {
  QuestionSearchInput,
  PracticeSubmitSearchType,
  InstructorPracticeSubmitSearchType,
} from '../../../../../../gen/graphql';
import { PracticeSubmitSearchInputParams, PracticeSubmitTabContent } from '../PracticeSubmitTabContent';
import { Loader } from '../../../../../molecules/Loader';
import ForbiddenPage from '../../../../public/Forbidden';
import { useCommonModal } from '../../../../../../redux/common_modal/useCommonModal';
import { PracticeDetailFunctions } from './PracticeDetailFunctions';
import { PracticeDetailContent } from './PracticeDetailContent';

/** タブ */
type TabType = 'body' | 'list' | 'questions' | 'submits';

export function PracticeDetail(props: { tab: TabType }): React.ReactElement {
  const pathID = useParams<{ practice_id: string }>().practice_id;
  if (!pathID) return NotFoundPage;

  return <Internal practiceID={pathID} tab={props.tab} />;
}

interface PracticeDetailProps {
  practiceID: string;
  tab: TabType;
}

function Internal(props: PracticeDetailProps): React.ReactElement {
  const { openModal } = useCommonModal();
  const [isQuestionCreateModalOpen, setIsQuestionCreateModalOpen] = useState(false);

  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  const navigate = useNavigate();

  const { user, permissionCheck } = useUser();

  const permissions = PracticeDetailFunctions.getPermissions(permissionCheck);

  const getPracticeResult = PracticeDetailFunctions.useGetPractice(props.practiceID, (practice) => {
    setMetaTitle(`${practice?.title} | ${LOWER_META_TITLE}`);
    setMetaDescription(`侍テラコヤの課題ページです。この課題では、${practice?.description}`);
  });

  const practice = getPracticeResult.data?.practice;

  const program = practice?.program;
  const programElements = getChaptersAndPractices(program);

  const getQuestionsResult = PracticeDetailFunctions.useGetQuestions(props.practiceID);
  const questionsTotal = getQuestionsResult.data?.getQuestions.total ?? 0;
  const questionsItems = getQuestionsResult.data?.getQuestions.items ?? [];
  const fetchQuestions = (params: QuestionSearchInput) => {
    getQuestionsResult.refetch({ input: params });
  };

  const getPracticeSubmitsResult = PracticeDetailFunctions.useGetPracticeSubmits(
    props.practiceID,
    permissions.practiceForInstructorAndCoachRead,
  );

  const getPracticeSubmitsForInstructorResult = PracticeDetailFunctions.useGetPracticeSubmitsForInstructor(
    props.practiceID,
    !permissions.practiceForInstructorAndCoachRead,
  );

  const fetchPracticeSubmits = (params: PracticeSubmitSearchInputParams) => {
    if (permissions.practiceForInstructorAndCoachRead) {
      getPracticeSubmitsForInstructorResult.refetch({
        input: {
          type: InstructorPracticeSubmitSearchType.All,
          practiceID: props.practiceID,
          ...params,
        },
      });
    } else {
      getPracticeSubmitsResult.refetch({
        input: {
          type: PracticeSubmitSearchType.All,
          practiceID: props.practiceID,
          ...params,
        },
      });
    }
  };
  const practiceSubmits =
    getPracticeSubmitsResult.data?.getPracticeSubmits.items ??
    getPracticeSubmitsForInstructorResult.data?.getPracticeSubmitsForInstructor.items ??
    [];
  const practiceSubmitsTotal =
    getPracticeSubmitsResult.data?.getPracticeSubmits.total ??
    getPracticeSubmitsForInstructorResult.data?.getPracticeSubmitsForInstructor.total ??
    0;

  const handleNewQuestionClick = () => {
    if (openModal(FunctionType.Question, PermissionType.Create)) return;
    setIsQuestionCreateModalOpen(true);
  };

  const handleQuestionCreateModalLink = () => {
    window.open(
      `/question/new?category_id=${program?.category}&program_id=${program?.id}&practice_id=${props.practiceID}`,
    );
    setIsQuestionCreateModalOpen(false);
  };

  const tabs = [
    {
      label: '本文',
      to: `/practices/${props.practiceID}/body`,
      active: props.tab === 'body',
    },
    {
      label: '目次',
      to: `/practices/${props.practiceID}/list`,
      active: props.tab === 'list',
    },
    {
      label: `質問一覧 ${questionsTotal}件`,
      to: `/practices/${props.practiceID}/questions`,
      active: props.tab === 'questions',
    },
    {
      label: `提出物一覧 ${practiceSubmitsTotal}件`,
      to: `/practices/${props.practiceID}/submits`,
      active: props.tab === 'submits',
    },
  ];

  const breadcrumbs = [
    {
      label: 'ホーム',
      to: '/home',
    },
    {
      label: '教材',
      to: permissions.programForInstructorAndCoachRead ? '/programs?type=1' : '/programs',
    },
    {
      label: program?.title ? program.title : '章一覧',
      to: program?.id ? `/programs/${program?.id}/chapters` : '',
    },
    {
      label: practice?.title ? practice.title : '現在のページ',
    },
  ];

  const showLoader =
    getPracticeResult.loading ||
    getQuestionsResult.loading ||
    getPracticeSubmitsResult.loading ||
    getPracticeSubmitsForInstructorResult.loading;

  // 課題データ取得できない場合は403へ
  if (!practice && !getPracticeResult.loading) return ForbiddenPage;

  return (
    <>
      <Loader display={showLoader} />
      <QuestionCreateModal
        isOpen={isQuestionCreateModalOpen}
        onClose={() => setIsQuestionCreateModalOpen(false)}
        handleLink={handleQuestionCreateModalLink}
      />
      <TabNavigationLayout pageTitle="課題" tabs={tabs} unfixed metaTitle={metaTitle} metaDescription={metaDescription}>
        <PageWrapper>
          <Main>
            <StyledBreadcrumb links={breadcrumbs} />
            {props.tab === 'body' ? (
              <PracticeDetailContent
                practiceID={props.practiceID}
                practice={practice}
                hasPermissionToPracticeForInstructorAndCoachRead={permissions.practiceForInstructorAndCoachRead}
                onViewPracticeExplanationButtonClick={(practiceID) => navigate(`/practices/${practiceID}/explanation`)}
                hasPermissionToCreatePracticeSubmit={() =>
                  openModal(FunctionType.PracticeSubmit, PermissionType.Create)
                }
                onPracticeSubmitCreateButtonClick={(practiceID) => navigate(`/practices/${practiceID}/submits/new`)}
                hasPermissionToUpdatePracticeSubmit={() =>
                  openModal(FunctionType.PracticeSubmit, PermissionType.Update)
                }
                onPracticeSubmitUpdateButtonClick={(practiceSubmitID, practiceSubmitDetailID) =>
                  navigate(`/practices/submits/${practiceSubmitID}/details/${practiceSubmitDetailID}/edit`)
                }
                onViewFeedbackButtonClick={(practiceSubmitID) => navigate(`/practices/submits/${practiceSubmitID}`)}
                isLoggedIn={user.isLogged}
              />
            ) : props.tab === 'list' ? (
              <ProgramElementsList
                programElements={programElements}
                currentID={props.practiceID}
                programID={program?.id}
              />
            ) : props.tab === 'submits' ? (
              <>
                {program && (
                  <PracticeSubmitTabContent
                    practiceSubmits={practiceSubmits}
                    total={practiceSubmitsTotal}
                    fetch={fetchPracticeSubmits}
                  />
                )}
              </>
            ) : props.tab === 'questions' ? (
              <>
                {program && (
                  <QuestionTabContent
                    permissionCheck={permissionCheck}
                    questionsTotal={questionsTotal}
                    questionsItems={questionsItems}
                    fetchQuestions={fetchQuestions}
                    program={program}
                    practiceID={props.practiceID}
                  />
                )}
              </>
            ) : (
              // ここに来ることはない。
              <></>
            )}
          </Main>
          {(permissions.questionCreate ||
            permissions.recommendLightRead ||
            permissions.recommendTeamSubscriptionRead ||
            permissions.recommendTeamSubscriptionToAdminRead) && <Fab onClick={handleNewQuestionClick}>+ 質問する</Fab>}
        </PageWrapper>
      </TabNavigationLayout>
    </>
  );
}

const Main = styled.div`
  transition: all 0.2s;
`;
const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 1rem;

  ${media.lessThan('medium')`
    display: none;
  `}
`;
